/**
 * Browser-side reminders for upcoming TV programs.
 * Persists to localStorage, schedules native Web Notifications.
 * No backend required — fully client-side.
 */
import { logger } from "./logger";

const STORAGE_KEY = "fedevo:reminders";
const LEAD_TIME_MS = 60 * 1000;  // notify 1 min before program start

export const getReminders = () => {
  if (typeof window === "undefined") return [];
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
  } catch (err) {
    logger.warn("[reminders] could not parse localStorage:", err);
    return [];
  }
};

const save = (list) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  // Broadcast to other tabs / listeners
  window.dispatchEvent(new CustomEvent("fedevo:reminders-updated"));
};

const reminderKey = (videoId, fireAt) => `${videoId}@${fireAt}`;

export const addReminder = (r) => {
  const list = getReminders();
  const key = reminderKey(r.videoId, r.fireAt);
  if (list.some(x => reminderKey(x.videoId, x.fireAt) === key)) return null;
  const item = { id: key, createdAt: new Date().toISOString(), ...r };
  list.push(item);
  save(list);
  return item;
};

export const removeReminder = (videoId, fireAt) => {
  const key = reminderKey(videoId, fireAt);
  save(getReminders().filter(r => r.id !== key));
};

export const hasReminderFor = (videoId, fireAt) => {
  const key = reminderKey(videoId, fireAt);
  return getReminders().some(r => r.id === key);
};

export const isNotificationsSupported = () =>
  typeof window !== "undefined" && "Notification" in window;

export const getPermissionState = () =>
  isNotificationsSupported() ? Notification.permission : "unsupported";

export const requestNotificationPermission = async () => {
  if (!isNotificationsSupported()) return "unsupported";
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  return await Notification.requestPermission();
};

/** Fire a notification immediately. Caller must ensure permission is granted. */
export const fireNotification = ({ title, body, url }) => {
  if (!isNotificationsSupported() || Notification.permission !== "granted") return;
  try {
    const n = new Notification(title, {
      body,
      icon: "/favicon.ico",
      tag: url || title,
    });
    n.onclick = () => {
      try { window.focus(); } catch (err) {
        logger.warn("[reminders] window.focus failed:", err);
      }
      if (url) window.location.assign(url);
      n.close();
    };
  } catch (err) {
    logger.warn("[reminders] Notification dispatch failed:", err);
  }
};

/** Time-until-fire in ms (negative if already passed). Includes lead time. */
export const msUntilFire = (fireAt) => new Date(fireAt).getTime() - LEAD_TIME_MS - Date.now();

export const LEAD_TIME_LABEL = "1 min avant";
